// Kitchen-layout presets — used by NewProjectModal to spawn a project +
// walls + a starter set of cabinets in one click.
//
// All dimensions in inches. Coordinates follow Wall convention: floor
// plane is XZ; walls are line segments {a:[x,z], b:[x,z]}.

import type { Cabinet, Wall } from "@/lib/types";

export type LayoutId = "empty" | "straight" | "l_shape" | "u_shape";

interface StarterCabinet {
  classId:  "BaseCabinet" | "WallCabinet" | "TallCabinet";
  width:    number;
  height:   number;
  depth:    number;
  position: [number, number, number];
  rotation: number;
  anchor:   Cabinet["anchor"];
}

export interface LayoutPreset {
  id:        LayoutId;
  label:     string;
  hint:      string;
  /** Default room — used if user doesn't tweak the dim slider. */
  defaultRoom(opts: LayoutOpts): { walls: Wall[]; height: number };
  /** Cabinets to spawn on project creation. */
  starterCabinets(opts: LayoutOpts): StarterCabinet[];
}

export interface LayoutOpts {
  /** Primary wall length (inches). For L/U, used for the long wall. */
  primaryLength: number;
  /** Secondary wall length. For L = the short leg; for U = the side legs. */
  secondaryLength: number;
  /** Ceiling height. */
  ceilingHeight: number;
}

const WALL_T = 4;

// ── helpers ──────────────────────────────────────────────────────

function baseRow(startX: number, count: number, eachW = 36): StarterCabinet[] {
  const out: StarterCabinet[] = [];
  let x = startX;
  for (let i = 0; i < count; i++) {
    out.push({
      classId: "BaseCabinet",
      width: eachW, height: 34.5, depth: 24,
      position: [x, 0, 4],          // 4" off the back wall (wall thickness)
      rotation: 0,
      anchor: "floor",
    });
    x += eachW;
  }
  return out;
}

function wallRow(startX: number, count: number, eachW = 30): StarterCabinet[] {
  const out: StarterCabinet[] = [];
  let x = startX;
  for (let i = 0; i < count; i++) {
    out.push({
      classId: "WallCabinet",
      width: eachW, height: 30, depth: 12,
      position: [x, 54, 4],          // 54" off the floor
      rotation: 0,
      anchor: "wall",
    });
    x += eachW;
  }
  return out;
}

// ── presets ──────────────────────────────────────────────────────

const empty: LayoutPreset = {
  id: "empty",
  label: "Empty room",
  hint: "Just the room — start designing from scratch.",
  defaultRoom: (o) => ({
    walls: rect(o.primaryLength, o.secondaryLength, o.ceilingHeight),
    height: o.ceilingHeight,
  }),
  starterCabinets: () => [],
};

const straight: LayoutPreset = {
  id: "straight",
  label: "Galley (one wall)",
  hint: "Run of base + wall cabinets along the back wall.",
  defaultRoom: (o) => ({
    walls: rect(o.primaryLength, Math.max(60, o.secondaryLength), o.ceilingHeight),
    height: o.ceilingHeight,
  }),
  starterCabinets: (o) => {
    // Fit 36"-wide base cabinets across the wall, leaving 2" margin each side.
    const usable = Math.max(0, o.primaryLength - 4);
    const n = Math.max(1, Math.floor(usable / 36));
    return [...baseRow(2, n), ...wallRow(2, n)];
  },
};

const lShape: LayoutPreset = {
  id: "l_shape",
  label: "L-shape (two walls)",
  hint: "Corner kitchen. Base + wall units along each leg.",
  defaultRoom: (o) => ({
    walls: rect(o.primaryLength, o.secondaryLength, o.ceilingHeight),
    height: o.ceilingHeight,
  }),
  starterCabinets: (o) => {
    const main = Math.max(0, o.primaryLength - 24 - 4);   // leave space for corner
    const side = Math.max(0, o.secondaryLength - 24 - 4);
    const mainN = Math.max(1, Math.floor(main / 36));
    const sideN = Math.max(1, Math.floor(side / 36));
    const mainBases = baseRow(2, mainN);
    const sideBases = baseRow(2, sideN).map((c) => ({
      ...c,
      position: [4, 0, 36 + (c.position[0] - 2)] as [number, number, number],
      rotation: Math.PI / 2,
    }));
    const mainWalls = wallRow(2, mainN);
    const sideWalls = wallRow(2, sideN).map((c) => ({
      ...c,
      position: [4, 54, 36 + (c.position[0] - 2)] as [number, number, number],
      rotation: Math.PI / 2,
    }));
    return [...mainBases, ...sideBases, ...mainWalls, ...sideWalls];
  },
};

const uShape: LayoutPreset = {
  id: "u_shape",
  label: "U-shape (three walls)",
  hint: "Three-sided kitchen. Cabinets on every wall.",
  defaultRoom: (o) => ({
    walls: rect(o.primaryLength, o.secondaryLength, o.ceilingHeight),
    height: o.ceilingHeight,
  }),
  starterCabinets: (o) => {
    const main = Math.max(0, o.primaryLength - 48 - 4);
    const side = Math.max(0, o.secondaryLength - 24 - 4);
    const mainN = Math.max(1, Math.floor(main / 36));
    const sideN = Math.max(1, Math.floor(side / 36));
    const mainBases = baseRow(26, mainN);
    const leftBases = baseRow(2, sideN).map((c) => ({
      ...c,
      position: [4, 0, 36 + (c.position[0] - 2)] as [number, number, number],
      rotation: Math.PI / 2,
    }));
    const rightBases = baseRow(2, sideN).map((c) => ({
      ...c,
      position: [o.primaryLength - 4, 0, 36 + (c.position[0] - 2)] as [number, number, number],
      rotation: -Math.PI / 2,
    }));
    const mainWalls = wallRow(26, mainN);
    return [...mainBases, ...leftBases, ...rightBases, ...mainWalls];
  },
};

function rect(W: number, D: number, H: number): Wall[] {
  return [
    { a: [0, 0], b: [W, 0], height: H, thickness: WALL_T },
    { a: [W, 0], b: [W, D], height: H, thickness: WALL_T },
    { a: [W, D], b: [0, D], height: H, thickness: WALL_T },
    { a: [0, D], b: [0, 0], height: H, thickness: WALL_T },
  ];
}

export const LAYOUTS: LayoutPreset[] = [empty, straight, lShape, uShape];

export const LAYOUT_DEFAULTS: LayoutOpts = {
  primaryLength:   144,  // 12 ft
  secondaryLength: 108,  // 9 ft
  ceilingHeight:    96,  // 8 ft
};

export function getLayout(id: LayoutId): LayoutPreset {
  return LAYOUTS.find((l) => l.id === id) ?? empty;
}
